// src/pages/CRM/Core/CRMDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions,
  Button, Chip, Avatar, Divider, CircularProgress, Alert,
  Tab, Tabs, IconButton, Tooltip, Stack
} from '@mui/material';
import {
  People as ContactsIcon,
  Business as AccountsIcon,
  PersonAdd as LeadsIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Event as MeetingIcon,
  Note as NoteIcon,
  LocalOffer as TagIcon
} from '@mui/icons-material';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';
import {
  LeadStatusLabels, LeadStatusColors,
  LeadScoreLabels, LeadScoreColors,
  ActivityTypeLabels
} from './crmEnums';

const CRMDashboard = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    document.title = 'CRM | KlonIT Web Application';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadDashboard();
    }
  }, [canView, chatbotId]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
    } catch (err) {
      console.error('Error checking permissions:', err);
    }
  };

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const response = await axiosInstance.get('/api/crm/dashboard', { params });
      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (err) {
      setError('Failed to load CRM dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'EMAIL': return <EmailIcon fontSize="small" />;
      case 'CALL': return <PhoneIcon fontSize="small" />;
      case 'MEETING': return <MeetingIcon fontSize="small" />;
      case 'NOTE': return <NoteIcon fontSize="small" />;
      default: return <NoteIcon fontSize="small" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view the CRM module.
      </Alert>
    );
  }

  const cards = [
    {
      title: 'Contacts',
      description: 'Manage your contact database with detailed profiles, relationship mapping, and activity tracking.',
      icon: <ContactsIcon sx={{ fontSize: 60, color: '#1976d2' }} />,
      count: dashboardData?.contacts?.total || 0,
      link: `/chatbot/${chatbotId}/crm/contacts`,
      color: '#1976d2'
    },
    {
      title: 'Clients',
      description: 'Track companies and organizations with hierarchy management and stakeholder relationships.',
      icon: <AccountsIcon sx={{ fontSize: 60, color: '#2e7d32' }} />,
      count: dashboardData?.accounts?.total || 0,
      link: `/chatbot/${chatbotId}/crm/clients`,
      color: '#2e7d32'
    },
    {
      title: 'Leads',
      description: 'Capture and qualify potential customers with lead scoring and conversion tracking.',
      icon: <LeadsIcon sx={{ fontSize: 60, color: '#ed6c02' }} />,
      count: dashboardData?.leads?.total || 0,
      link: `/chatbot/${chatbotId}/crm/leads`,
      color: '#ed6c02'
    }
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            CRM
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customer Relationship Management
          </Typography>
        </Box>
      </Box>

      {/* Main Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: '0.3s',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
              onClick={() => navigate(card.link)}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: `${card.color}15`,
                    margin: '0 auto 16px'
                  }}
                >
                  {card.icon}
                </Avatar>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  {card.title}
                </Typography>
                <Typography variant="h3" color={card.color} fontWeight={700} sx={{ mb: 2 }}>
                  {card.count.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ bgcolor: card.color }}
                >
                  Manage
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Lead Stats and Recent Activity */}
      <Grid container spacing={3}>
        {/* Lead Stats */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Lead Pipeline
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              By Status
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }}>
              {dashboardData?.leads?.by_status && Object.entries(dashboardData.leads.by_status).map(([status, count]) => (
                <Chip
                  key={status}
                  label={`${LeadStatusLabels[status] || status}: ${count}`}
                  color={LeadStatusColors[status] || 'default'}
                  size="small"
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              By Score
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {dashboardData?.leads?.by_score && Object.entries(dashboardData.leads.by_score).map(([score, count]) => (
                <Chip
                  key={score}
                  label={`${LeadScoreLabels[score] || score}: ${count}`}
                  color={LeadScoreColors[score] || 'default'}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>

            {canModify && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/chatbot/${chatbotId}/crm/leads?action=new`)}
                >
                  Add New Lead
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Recent Activity
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {dashboardData?.recent_activities?.length > 0 ? (
              <Stack spacing={2}>
                {dashboardData.recent_activities.slice(0, 5).map((activity, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1
                    }}
                  >
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                      {getActivityIcon(activity.activity_type)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2">
                        {activity.subject}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ActivityTypeLabels[activity.activity_type] || activity.activity_type} •{' '}
                        {new Date(activity.activity_date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No recent activity
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      {canModify && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Quick Actions
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<ContactsIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/contacts?action=new`)}
            >
              New Contact
            </Button>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<AccountsIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/clients?action=new`)}
            >
              New Client
            </Button>
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<LeadsIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/leads?action=new`)}
            >
              New Lead
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<TagIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/tags`)}
            >
              Manage Tags
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default CRMDashboard;